import { startLazyCovers } from "./components.ts";
import { clear, h, link } from "./dom.ts";
import { icon } from "./icons.ts";
import { botPage } from "./pages/bot.ts";
import { browsePage } from "./pages/browse.ts";
import { clipPage, feedPage } from "./pages/clips.ts";
import { postPage, peopleFeedPage } from "./pages/people-feed.ts";
import { friendsPage, xImportPage } from "./pages/friends.ts";
import { editProfilePage } from "./pages/profile-people.ts";
import { creatorPage } from "./pages/creator.ts";
import { avatarPage } from "./pages/avatar.ts";
import { checkoutReturnPage } from "./pages/checkout.ts";
import { dashboardPage } from "./pages/dashboard.ts";
import { developersPage } from "./pages/developers.ts";
import { applyPage } from "./pages/apply.ts";
import { gamePage } from "./pages/game.ts";
import { hauntGuidePage, hauntPage } from "./pages/haunt.ts";
import { homePage, prerenderHome } from "./pages/home.ts";
import { signinPage } from "./pages/signin.ts";
import { meHomePage } from "./pages/me-home.ts";
import { earnPage, membershipPage } from "./pages/membership.ts";
import { profilePage } from "./pages/me-profile.ts";
import { settingsPage } from "./pages/me-settings.ts";
import { welcomePage } from "./pages/me-welcome.ts";
import { assetPage, storeBrowsePage, storeCreatorPage, storeQueryWantsBrowse } from "./pages/store.ts";
import { navigate, rerender, route, setTitle, startRouter } from "./router.ts";
import { currentMe, loadMe, nameOf, onSessionChange, signedIn, signOut } from "./session.ts";
import { loadViewerCurrency } from "./money.ts";
import { loadSelling, selling } from "./selling.ts";
import { flushPlaytime } from "./playtime.ts";
import { getCatalogue, getHome } from "./catalogue.ts";
import { inSeason } from "./rows.ts";
import { plazaNavLink } from "./plaza.ts";
import { allZones } from "./zones/registry.ts";
import { zoneRoute } from "./zones/page.ts";
import "./zones/open-haunt/index.ts"; // Open Haunt registers its sections, 3D hero and Home portal
import "./zones/download/index.ts"; // The Gate registers its sections (platform, requirements, …)
import "./zones/developers/index.ts"; // The Forge registers its sections (/developers and the signed-out /dashboard)
import "./zones/creators/index.ts"; // Studio Row registers its spotlights and directory
import "./zones/events/index.ts"; // The Stage registers its programme sections and the night's Home portal
import { bell, closeBellOutside } from "./bell.ts"; // wave 13: the bell and inbox (w13-notify)
import { inboxPage } from "./pages/me-inbox.ts";
import "./zones/assets/index.ts"; // the Workshop registers its sections and its live generator hero
import "./zones/category/index.ts"; // category worlds (wave 13): the featured hero, chips, category rows and banners

// the zone shell's styles (public/zones.css), ahead of the first render
document.head.append(h("link", { rel: "stylesheet", href: "/zones.css" }));

route("/", homePage);
route("/browse", browsePage);
route("/g/:id", gamePage);
route("/c/:id", creatorPage);
// /download is the download zone (The Gate) since its design PASS on 19 Oct; see ZONES_AT_SLUG
route("/clip/:id", clipPage);
route("/feed", peopleFeedPage); // wave 13: the people feed (posts); clips keep /clips
route("/clips", feedPage); // the clip feed (community)
route("/p/:id", postPage);
route("/friends", friendsPage);
route("/friends/x", xImportPage);
// /events is the events zone (The Stage) since its design PASS on 19 Oct (docs/design/reviews/events-2026-10-19.md); see ZONES_AT_SLUG
// /store: the filtered browse, or the Workshop zone for a bare /store once "assets" is in ZONES_AT_SLUG (read per request)
route("/store", (p, q) => (ZONES_AT_SLUG.includes("assets") && !storeQueryWantsBrowse(q) ? zoneRoute("assets")(p, q) : storeBrowsePage(p, q)));
route("/store/a/:creator/:name", assetPage);
route("/store/c/:handle", storeCreatorPage);
route("/signin", signinPage);
route("/haunt", hauntPage);
route("/haunt/guide", hauntGuidePage);
route("/developers", developersPage);
route("/membership", membershipPage);
route("/earn", earnPage);
route("/apply/:code", applyPage); // founding developers: the invite flow (docs/build/founding-devs.md)
route("/bot", botPage);
route("/checkout/:state", checkoutReturnPage);
route("/me/avatar", avatarPage);
// account track (wave 12): first run, your space, settings, public profiles
route("/welcome", welcomePage);
route("/me", meHomePage);
route("/me/settings", settingsPage);
route("/me/profile", editProfilePage);
route("/me/inbox", inboxPage);
route("/u/:handle", profilePage);
route("/dashboard", dashboardPage);
route("/dashboard/:section", dashboardPage);
route("/dashboard/g/:game", dashboardPage);
route("/dashboard/g/:game/:section", dashboardPage);
// zones (wave 12): every zone previews at /z/<id>. A zone takes its own /<slug> only once it's listed here (its
// owner asks w12-zones when it's ready to replace the page there); until then /haunt, /events, /store… stay the
// pages above, and links to a zone go to whichever page holds its slug (zones/registry.ts `zoneHref`).
const ZONES_AT_SLUG: string[] = ["events", "assets", "download", "creators"]; // design PASS 2026-10-19 (docs/design/reviews/)
route("/z/:id", zoneRoute());
for (const z of allZones()) if (ZONES_AT_SLUG.includes(z.id)) route(`/${z.slug}`, zoneRoute(z.id)); // /store is above

function notFound(params: Record<string, string>): HTMLElement {
  setTitle("Not found");
  return h(
    "div",
    { class: "empty" },
    h("h1", null, params.error ? "Something went wrong" : "Page not found"),
    h("p", { class: "dim" }, params.error ?? "That page doesn't exist."),
    link("/", { class: "btn" }, "Home"),
  );
}

/** The global bar's Plaza entry: not a page, it opens the world (plaza.ts). */
const PLAZA = "#plaza";

/** The pages the launcher's rail lists, as the global bar's links (Feed only when signed in; Friends always, as it sells the network signed out). */
const GLOBAL_NAV: [string, string][] = [
  ["/", "Home"],
  ["/browse", "Browse"],
  ["/store", "Assets"],
  [PLAZA, "Plaza"],
  ["/events", "Events"],
  ["/feed", "Feed"],
  ["/friends", "Friends"],
  ["/download", "Download"],
  ["/dashboard", "For creators"],
];

/**
 * Below 420 px the bar keeps these in its row and puts the rest under More (agreed with w13-plaza, wave 13). Home is
 * the mark and Download lives in the footer at that width, so neither goes in More.
 */
const PHONE_PRIMARY = new Set(["/browse", "/store", PLAZA, "/friends"]);

function navBar(me: ReturnType<typeof currentMe>): HTMLElement {
  const items = (season ? [...GLOBAL_NAV.slice(0, 2), ["/haunt", "Open Haunt"] as [string, string], ...GLOBAL_NAV.slice(2)] : GLOBAL_NAV); // the Feed shows Latest signed out (BANGERS.md slice 3)
  const extra = items.filter(([href]) => !PHONE_PRIMARY.has(href) && href !== "/" && href !== "/download" && href !== PLAZA);
  return h(
    "nav",
    { class: "global-nav", "aria-label": "Main" },
    // the Plaza entry launches the app (phones: /events#venues), so it's never a plain link (plaza.ts)
    items.map(([href, text]) => (href === PLAZA ? plazaNavLink() : link(href, { class: `global-link${PHONE_PRIMARY.has(href) ? "" : " phone-more"}`, "aria-current": current(href) }, text))),
    extra.length
      ? h(
          "details",
          { class: "nav-more" },
          h("summary", { class: "global-link", "aria-current": extra.some(([href]) => current(href)) ? "page" : null }, "More"),
          h("div", { class: "nav-more-list" }, extra.map(([href, text]) => link(href, { "aria-current": current(href) }, text))),
        )
      : null,
  );
}

/** Open Haunt joins the bar while the catalogue shows the Halloween row (accounts' clock, not this browser's). */
let season = false;
// Home reads the slim home payload (it has the rows); every other page loads the full catalogue anyway, so share it
void (location.pathname === "/" ? getHome() : getCatalogue())
  .then((c) => {
    if (inSeason(c) && !season) {
      season = true;
      if (document.querySelector(".global-nav")) header();
    }
  })
  .catch(() => {});

const current = (href: string) => {
  const here = location.pathname;
  return here === href || (href !== "/" && here.startsWith(href)) ? "page" : null;
};

/** False until the session has loaded: the bar draws without Sign in or the account menu, so neither flashes. */
let sessionKnown = false;

/** The global bar, after the Epic web store's: the mark and wordmark with the rail's pages, then the account and Get Banger. */
function header(): void {
  const top = document.getElementById("top")!;
  const me = currentMe();
  clear(
    top,
    h(
      "div",
      { class: "globalbar-in" },
      link("/", { class: "brand", "aria-label": "Banger home" }, h("img", { src: "/mark/banger-mark.svg", alt: "", width: 28, height: 28 }), h("span", { class: "wordmark" }, "Banger")),
      navBar(me),
      h("span", { class: "topbar-fill" }),
      h(
        "div",
        { class: "account" },
        // wave 13 (w13-notify): the bell sits with the account menu, so the phone nav row keeps its line
        sessionKnown && me ? bell(me.id) : null,
        !sessionKnown
          ? null
          : me
          ? h(
              "details",
              { class: "menu" },
              h("summary", { "aria-label": "Account menu" }, h("span", { class: "avatar-badge small", "aria-hidden": "true" }, nameOf(me).replace("@", "").slice(0, 1).toUpperCase()), h("span", { class: "menu-name" }, nameOf(me))),
              h(
                "div",
                { class: "menu-list" },
                link("/me", null, "Your space"),
                link("/feed", null, "Your feed"),
                link("/friends", null, "Friends"),
                link("/me/profile", null, "Edit profile"),
                link("/me/avatar", null, "My avatar"),
                link("/me/settings", null, "Settings"),
                link("/membership", null, "Membership"),
                link("/earn", null, "How creators earn"),
                link("/dashboard", null, "Creator dashboard"),
                h(
                  "button",
                  {
                    type: "button",
                    onclick: async () => {
                      await signOut();
                      navigate("/");
                    },
                  },
                  "Sign out",
                ),
              ),
            )
          : link(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`, { class: "global-link" }, "Sign in"),
      ),
      location.pathname === "/download" ? null : link("/download", { class: "btn small get" }, "Get Banger"),
    ),
  );
  topbar();
}

/** The store bar under it: Epic's search and Discover / Browse tabs. */
function topbar(): void {
  const bar = document.getElementById("topbar");
  if (!bar) return;
  const here = location.pathname;
  // a zone page sets body[data-zone] as it renders; any other page clears it
  if (!document.querySelector("#main > .zone")) delete document.body.dataset.zone;
  document.body.dataset.route = here === "/" ? "home" : here.split("/")[1] || "home";
  const q = new URLSearchParams(location.search).get("q") ?? "";
  const input = h("input", { type: "search", name: "q", value: here === "/browse" ? q : "", placeholder: "Search games", "aria-label": "Search games", autocomplete: "off" });
  const tab = (href: string, text: string, on: boolean) => link(href, { class: "tab", "aria-current": on ? "page" : null }, text);
  clear(
    bar,
    h(
      "div",
      { class: "topbar-in" },
      h(
        "form",
        {
          class: "topbar-search",
          role: "search",
          action: "/browse",
          onsubmit: (e: Event) => {
            e.preventDefault();
            const v = (input as HTMLInputElement).value.trim();
            navigate(v ? `/browse?q=${encodeURIComponent(v)}` : "/browse");
          },
        },
        icon("search", 14),
        input,
      ),
      h("nav", { class: "tabs-line", "aria-label": "Sections" }, tab("/", "Discover", here === "/"), tab("/browse", "Browse", here === "/browse")),
    ),
  );
}

function footer(): void {
  clear(
    document.getElementById("foot")!,
    h("span", { class: "wordmark small" }, "Banger"),
    h(
      "nav",
      { "aria-label": "Footer" },
      link("/browse", null, "Browse"),
      link("/store", null, "Assets"),
      link("/events", null, "Events"),
      link("/download", null, "Download"),
      link("/haunt", null, "Open Haunt"),
      link("/developers", null, "Developers"),
      link("/dashboard", null, "For creators"),
      link("/earn", null, "How creators earn"),
      link("/membership", null, "Membership"),
    ),
    h("span", { class: "dim small" }, "Good games are bangers. Games belong to their creators."),
  );
}

onSessionChange(() => {
  header();
  rerender();
  // Prices follow the viewer's wallet currency once signed in (USD signed out or before the first top-up).
  if (selling()) void loadViewerCurrency(signedIn()).then(rerender);
  // "Play in browser" sessions queued while signed out, offline or on a closed tab
  if (signedIn()) void flushPlaytime();
});

// close the account menu after picking something; the bell's panel on a click elsewhere
document.addEventListener("click", closeBellOutside);
document.addEventListener("click", (e) => {
  const menu = document.querySelector("details.menu[open]");
  if (menu && (e.target as Element).closest(".menu-list a, .menu-list button")) menu.removeAttribute("open");
  const more = document.querySelector("details.nav-more[open]");
  if (more && (!(e.target as Element).closest(".nav-more") || (e.target as Element).closest(".nav-more-list a"))) more.removeAttribute("open");
});

// money's `selling` first: with it false (or money unreachable) no page shows prices, tips, earnings or checkout
// The bar, the footer and Home's skeleton paint at once (wave 12: Home was blank for 2-3 s); the account slot and
// the pages wait for the session and money's `selling`.
header();
footer();
startLazyCovers();
if (location.pathname === "/") prerenderHome(document.getElementById("main")!);
void Promise.all([loadMe(), loadSelling()])
  .then(() => (selling() ? loadViewerCurrency(signedIn()) : null))
  .then(() => {
    sessionKnown = true;
    header();
    startRouter(document.getElementById("main")!, notFound, header, () => loadSelling());
  });
